import React from "react";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { RadioButtonProps } from "./types";
import {
  radioButtonVariants,
  circleVariants,
  circleLabelClass,
  feedbackMessageClass,
  optionImageClass,
  optionTextClass,
  feedbackWrapperDivClass,
} from "./RadioButton.css";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import { tokens } from "@labxchange/design_tokens";
import { useIntl, defineMessages } from "react-intl";
import { X, Check } from "lucide-react";

export const messages = defineMessages({
  correctFeedback: {
    id: "Question.correctFeedback",
    defaultMessage: "Correct!",
    description:
      "Feedback text shown when the user selects the correct answer.",
  },
  incorrectFeedback: {
    id: "Question.incorrectFeedback",
    defaultMessage: "Not quite.",
    description:
      "Feedback text shown when the user selects an incorrect answer.",
  },
  optionLabel: {
    id: "Question.optionLabel",
    defaultMessage: "Option {displayLabel}",
    description:
      "Label for a given option, where {displayLabel} is A, B, C, etc.",
  },
});

export const RadioButton: React.FC<RadioButtonProps> = ({
  option: { equation = false, ...optionData },
  displayLabel,
  isSelected,
  isSubmitted,
  isCorrect,
  showFeedbackOnSubmit = true,
  isMostRecentlySubmitted = false,
  resetCount,
}) => {
  const getVariant = () => {
    if (isSubmitted && !isCorrect && showFeedbackOnSubmit) {
      return "incorrect";
    }
    if (isSubmitted && isCorrect && showFeedbackOnSubmit) {
      return "correct";
    }
    if (isSelected || (isSubmitted && !showFeedbackOnSubmit)) {
      return "selected";
    }
    return "default";
  };

  const variant = getVariant();
  const showFeedback = isSubmitted && showFeedbackOnSubmit;
  const intl = useIntl();

  // Generate stable announcement content - only for the most recently submitted option
  const getAnnouncementContent = () => {
    if (!showFeedback || !isMostRecentlySubmitted) {
      return ""; // No announcement
    }

    const feedbackText = isCorrect
      ? intl.formatMessage(messages.correctFeedback)
      : intl.formatMessage(messages.incorrectFeedback);

    const feedbackMessage =
      optionData.feedback && optionData.feedback.trim()
        ? intl.formatMessage({ id: optionData.feedback })
        : "";

    return `${feedbackText} ${feedbackMessage}`.trim();
  };

  // Generate accessible label for the option
  const getOptionLabel = () => {
    const labelPrefix = intl.formatMessage(messages.optionLabel, {
      displayLabel,
    });

    if (optionData.image && optionData.text) {
      const imageAlt =
        optionData.image.altText && optionData.image.altText.trim()
          ? intl.formatMessage({ id: optionData.image.altText })
          : "";
      const text =
        optionData.text && optionData.text.trim()
          ? intl.formatMessage({ id: optionData.text })
          : "";
      return `${labelPrefix}: ${imageAlt} - ${text}`;
    }

    if (optionData.image) {
      const imageAlt =
        optionData.image.altText && optionData.image.altText.trim()
          ? intl.formatMessage({ id: optionData.image.altText })
          : "";
      return `${labelPrefix}: ${imageAlt}`;
    }

    const text =
      optionData.text && optionData.text.trim()
        ? intl.formatMessage({ id: optionData.text })
        : "";
    return `${labelPrefix}: ${text}`;
  };

  return (
    <>
      <RadioGroup.Item
        value={optionData.id.toString()}
        className={radioButtonVariants[variant]}
        aria-label={getOptionLabel()}
      >
        {showFeedbackOnSubmit ? (
          <div className={circleVariants[variant]}>
            {showFeedback && !isCorrect ? (
              <X color={tokens.questionselector.fail.color_icon} />
            ) : showFeedback && isCorrect ? (
              <Check color={tokens.questionselector.success.color_icon} />
            ) : (
              <div className={circleLabelClass}>
                <p
                  aria-label={intl.formatMessage(messages.optionLabel, {
                    displayLabel,
                  })}
                >
                  {displayLabel}
                </p>
              </div>
            )}
          </div>
        ) : null}

        {/* Render content based on what's available: text, image, or both */}
        {optionData.image && optionData.text ? (
          // Both image and text
          <div>
            <img
              src={optionData.image.src}
              alt={optionData.image.altText}
              className={optionImageClass}
            />
            <div className={optionTextClass}>
              <RichText value={optionData.text} equation={equation} />
            </div>
          </div>
        ) : optionData.image ? (
          // Image only
          <div>
            <img
              src={optionData.image.src}
              alt={optionData.image.altText}
              className={optionImageClass}
            />
          </div>
        ) : (
          <span>
            <RichText value={optionData.text} equation={equation} />
          </span>
        )}
      </RadioGroup.Item>

      <div
        key={`feedback-${resetCount}`}
        aria-live="polite"
        role="status"
        aria-atomic="true"
        className={feedbackWrapperDivClass[variant]}
      >
        <p
          className={`${feedbackMessageClass[variant]}`}
          id={displayLabel + "-feedback"}
        >
          {showFeedback && (
            <>
              <span aria-label={getAnnouncementContent()}>
                {isCorrect ? (
                  <b>{intl.formatMessage(messages.correctFeedback)}</b>
                ) : (
                  <b>{intl.formatMessage(messages.incorrectFeedback)}</b>
                )}
              </span>{" "}
              <RichText value={optionData.feedback} />
            </>
          )}
        </p>
      </div>
    </>
  );
};

export default RadioButton;
